import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { profileAPI, oddsAPI } from '@/utils/api';
import { useLang } from '@/utils/i18n';
import { ArrowLeft, TrendingUp, BarChart3, Activity, Globe, Zap, Loader2, Bell, BellRing, X } from 'lucide-react';
import LangSwitcher from '@/components/LangSwitcher';
import { MobileBottomNav } from '@/components/MobileNav';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http:\/\//i, 'https://');

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [analytics, setAnalytics] = useState(null);
  const [odds, setOdds] = useState(null);
  const [selectedSport, setSelectedSport] = useState('Soccer');
  const [sportsInfo, setSportsInfo] = useState([]);
  const [globalStats, setGlobalStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [oddsLoading, setOddsLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [alertModal, setAlertModal] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => { loadData(); }, []);
  useEffect(() => { loadOdds(selectedSport); }, [selectedSport]);

  const loadData = async () => {
    try {
      const [analyticsRes, sportsRes, alertsRes] = await Promise.all([
        profileAPI.getAnalytics().catch(() => ({ data: null })),
        fetch(`${BACKEND_URL}/api/odds/sports`, { headers }).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/odds-alerts/`, { headers }).then(r => r.json()).catch(() => ({ alerts: [] })),
      ]);
      setAnalytics(analyticsRes.data);
      if (sportsRes.sports_detail) {
        setSportsInfo(sportsRes.sports_detail);
      }
      setAlerts(alertsRes.alerts || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const loadOdds = async (sport) => {
    setOddsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/odds/live/${encodeURIComponent(sport)}`, { headers });
      const data = await res.json();
      setOdds(data);
    } catch { /* ignore */ }
    setOddsLoading(false);
  };

  const loadGlobalStats = async () => {
    if (globalStats) return;
    try {
      const data = await fetch(`${BACKEND_URL}/api/odds/all`, { headers }).then(r => r.json());
      setGlobalStats(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadGlobalStats(); }, []);

  const createAlert = async (match, sport, team, threshold, direction) => {
    try {
      await fetch(`${BACKEND_URL}/api/odds-alerts/create`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ match, sport, team, threshold, direction }),
      });
      setAlertModal(null);
      const res = await fetch(`${BACKEND_URL}/api/odds-alerts/`, { headers }).then(r => r.json());
      setAlerts(res.alerts || []);
    } catch { /* ignore */ }
  };

  const deleteAlert = async (match) => {
    try {
      await fetch(`${BACKEND_URL}/api/odds-alerts/${encodeURIComponent(match)}`, {
        method: 'DELETE', headers,
      });
      setAlerts(prev => prev.filter(a => a.match !== match));
    } catch { /* ignore */ }
  };

  const hasAlert = (match) => alerts.some(a => a.match === match && a.active);

  const sportTabs = sportsInfo.length > 0
    ? sportsInfo.filter(s => s.leagues > 0).map(s => s.name)
    : ['Soccer', 'Basketball', 'Ice Hockey', 'Tennis', 'Baseball', 'MMA'];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#121212] flex items-center justify-center">
        <div className="text-[#00BFA6]">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#121212] pb-16 md:pb-0" data-testid="analytics-page">
      <header className="bg-[#1E1E1E] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4 flex justify-between items-center">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" className="text-gray-400 hover:text-white" data-testid="analytics-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> {t('back')}
          </Button>
          <h1 className="text-lg font-bold text-[#00BFA6]">{t('analytics_title')}</h1>
          <LangSwitcher />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 space-y-6">

        {/* Global Live Stats Banner */}
        <div data-testid="live-stats-banner" className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-[#00BFA6]/20 to-[#00BFA6]/5 border border-[#00BFA6]/30 rounded-xl p-4 text-center">
            <Globe className="w-6 h-6 text-[#00BFA6] mx-auto mb-2" />
            <div className="text-2xl md:text-3xl font-mono font-bold text-[#00BFA6]">
              {globalStats?.total_events?.toLocaleString() || odds?.total_events || '...'}
            </div>
            <div className="text-xs text-gray-400 mt-1">{lang === 'de' ? 'Live Events' : 'Live Events'}</div>
          </div>
          <div className="bg-gradient-to-br from-[#7B1FA2]/20 to-[#7B1FA2]/5 border border-[#7B1FA2]/30 rounded-xl p-4 text-center">
            <Zap className="w-6 h-6 text-[#7B1FA2] mx-auto mb-2" />
            <div className="text-2xl md:text-3xl font-mono font-bold text-[#7B1FA2]">
              {globalStats?.total_betting_options?.toLocaleString() || '...'}
            </div>
            <div className="text-xs text-gray-400 mt-1">{lang === 'de' ? 'Wettoptionen' : 'Betting Options'}</div>
          </div>
          <div className="bg-[#1E1E1E] border border-white/10 rounded-xl p-4 text-center">
            <Activity className="w-6 h-6 text-white mx-auto mb-2" />
            <div className="text-2xl md:text-3xl font-mono font-bold text-white">
              {globalStats?.total_leagues || sportsInfo.reduce((a, s) => a + s.leagues, 0) || '82'}
            </div>
            <div className="text-xs text-gray-400 mt-1">{lang === 'de' ? 'Ligen' : 'Leagues'}</div>
          </div>
          <div className="bg-[#1E1E1E] border border-white/10 rounded-xl p-4 text-center">
            <BarChart3 className="w-6 h-6 text-white mx-auto mb-2" />
            <div className="text-2xl md:text-3xl font-mono font-bold text-white">
              {sportTabs.length}
            </div>
            <div className="text-xs text-gray-400 mt-1">{lang === 'de' ? 'Sportarten' : 'Sports'}</div>
          </div>
        </div>

        {/* Events per Sport breakdown */}
        {globalStats?.by_sport && (
          <Card className="bg-[#1E1E1E] border-white/10">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Globe className="w-4 h-4 text-[#00BFA6]" />
                {lang === 'de' ? 'Events pro Sportart' : 'Events per Sport'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(globalStats.by_sport)
                  .sort((a, b) => b[1].betting_options - a[1].betting_options)
                  .filter(([, v]) => v.events > 0)
                  .map(([sport, info]) => {
                    const maxBets = Math.max(...Object.values(globalStats.by_sport).map(v => v.betting_options));
                    const pct = maxBets > 0 ? (info.betting_options / maxBets) * 100 : 0;
                    return (
                      <div key={sport} className="flex items-center gap-3 cursor-pointer hover:bg-white/5 rounded-lg p-1.5 transition-colors"
                           onClick={() => setSelectedSport(sport)}>
                        <span className="w-36 text-sm text-gray-300 truncate">{sport}</span>
                        <div className="flex-1 h-3 bg-white/5 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-[#00BFA6] to-[#7B1FA2] rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs font-mono text-[#00BFA6] w-16 text-right">{info.events}</span>
                        <span className="text-xs text-gray-500 w-24 text-right">{info.betting_options.toLocaleString()} Wetten</span>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Global Model Accuracy */}
        {analytics?.global_accuracy?.length > 0 && (
          <Card className="bg-[#1E1E1E] border-white/10">
            <CardHeader><CardTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-[#00BFA6]" /> {t('global_accuracy')}</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {analytics.global_accuracy.map((item) => (
                  <div key={item.sport} className="text-center p-3 bg-white/5 rounded-lg">
                    <div className="text-2xl font-mono font-bold text-[#00BFA6]">{item.accuracy}%</div>
                    <div className="text-xs text-gray-400 mt-1">{item.sport}</div>
                    <div className="text-xs text-gray-600">{item.total_tips} Tipps</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Live Odds */}
        <Card className="bg-[#1E1E1E] border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-[#00BFA6]" /> {t('live_odds')}
              </CardTitle>
              {odds?.total_events > 0 && (
                <span className="text-xs font-mono text-gray-400">
                  {odds.total_events} Events &middot; {odds.total_betting_options?.toLocaleString()} {lang === 'de' ? 'Wetten' : 'bets'}
                  {odds.leagues_fetched > 0 && ` &middot; ${odds.leagues_fetched} Ligen`}
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Sport tabs */}
            <div className="flex flex-wrap gap-2 mb-4" data-testid="odds-sport-tabs">
              {sportTabs.map((sport) => {
                const info = sportsInfo.find(s => s.name === sport);
                return (
                  <button
                    key={sport}
                    onClick={() => setSelectedSport(sport)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      selectedSport === sport
                        ? 'bg-[#00BFA6] text-black'
                        : 'bg-white/5 text-gray-400 hover:bg-white/10'
                    }`}
                  >
                    {info?.display_name || sport}
                    {info && <span className="ml-1 opacity-60">({info.leagues})</span>}
                  </button>
                );
              })}
            </div>

            {/* League breakdown */}
            {odds?.leagues?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {odds.leagues.map((lg, i) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 bg-white/5 border border-white/10 rounded-full text-gray-400">
                    {lg.title} ({lg.events})
                  </span>
                ))}
              </div>
            )}

            {/* Odds list */}
            <div className="space-y-3" data-testid="odds-list">
              {oddsLoading ? (
                <div className="text-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#00BFA6] mx-auto" />
                </div>
              ) : odds?.odds?.length > 0 ? (
                odds.odds.map((match, idx) => (
                  <div key={idx} data-testid={`odds-match-${idx}`} className="p-3 md:p-4 bg-[#121212] rounded-lg border border-white/5 hover:border-white/15 transition-colors">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <span className="font-medium text-sm block truncate">{match.match}</span>
                        {match.commence_time && (
                          <span className="text-[10px] text-gray-500">
                            {new Date(match.commence_time).toLocaleString(lang === 'de' ? 'de-DE' : 'en-US', {
                              day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                            })}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {match.total_bookmakers > 0 && (
                          <span className="text-[10px] text-gray-500 hidden sm:inline">{match.total_bookmakers} Buchmacher</span>
                        )}
                        <button
                          data-testid={`odds-alert-btn-${idx}`}
                          onClick={() => hasAlert(match.match)
                            ? deleteAlert(match.match)
                            : setAlertModal({ match: match.match, sport: selectedSport, home: match.home_team, away: match.away_team })
                          }
                          className={`p-1.5 rounded-lg transition-colors ${
                            hasAlert(match.match)
                              ? 'text-yellow-400 bg-yellow-400/10'
                              : 'text-gray-500 hover:text-yellow-400 hover:bg-yellow-400/10'
                          }`}
                          title={hasAlert(match.match) ? 'Alert entfernen' : 'Odds Alert setzen'}
                        >
                          {hasAlert(match.match) ? <BellRing className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      {match.bookmakers?.slice(0, 4).map((bm, bi) => (
                        <div key={bi} className="text-xs flex flex-wrap items-center gap-x-3">
                          <span className="text-gray-500 w-24 shrink-0 truncate">{bm.name}</span>
                          {bm.markets ? (
                            Object.entries(bm.markets).map(([mkt, odds_data]) => (
                              <span key={mkt} className="flex items-center gap-1">
                                <span className="text-gray-600 text-[10px]">[{mkt}]</span>
                                {Object.entries(odds_data).map(([name, price], oi) => (
                                  <span key={oi} className="ml-0.5">
                                    <span className="text-gray-400">{name}</span>{' '}
                                    <span className="font-mono text-[#00BFA6]">{typeof price === 'number' ? price.toFixed(2) : price}</span>
                                    {oi < Object.entries(odds_data).length - 1 && <span className="text-gray-700 mx-0.5">|</span>}
                                  </span>
                                ))}
                              </span>
                            ))
                          ) : bm.odds ? (
                            Object.entries(bm.odds).map(([name, price], oi) => (
                              <span key={oi}>
                                <span className="text-gray-400">{name}</span>{' '}
                                <span className="font-mono text-[#00BFA6]">{price}</span>
                                {oi < Object.entries(bm.odds).length - 1 && <span className="text-gray-700 mx-1">|</span>}
                              </span>
                            ))
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-gray-500 py-6">{t('no_odds')}</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Active Alerts */}
        {alerts.filter(a => a.active).length > 0 && (
          <Card className="bg-[#1E1E1E] border-white/10">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <BellRing className="w-4 h-4 text-yellow-400" />
                {lang === 'de' ? 'Aktive Odds Alerts' : 'Active Odds Alerts'} ({alerts.filter(a => a.active).length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {alerts.filter(a => a.active).map((alert, i) => (
                  <div key={i} data-testid={`active-alert-${i}`} className="flex items-center justify-between bg-[#121212] rounded-lg px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <div className="text-white truncate">{alert.match}</div>
                      <div className="text-[10px] text-gray-500">
                        {alert.team} · Quote {alert.direction === 'above' ? '>' : '<'} {alert.threshold}
                      </div>
                    </div>
                    <button
                      onClick={() => deleteAlert(alert.match)}
                      className="text-red-400 hover:text-red-300 p-1 shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Alert Modal */}
      {alertModal && (
        <div className="fixed inset-0 bg-black/70 z-[70] flex items-end md:items-center justify-center" onClick={() => setAlertModal(null)}>
          <div
            data-testid="alert-modal"
            className="bg-[#1E1E1E] border border-white/10 rounded-t-2xl md:rounded-2xl w-full max-w-md p-6"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-white mb-1">{lang === 'de' ? 'Odds Alert setzen' : 'Set Odds Alert'}</h3>
            <p className="text-xs text-gray-400 mb-4">{alertModal.match}</p>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-400 block mb-1">{lang === 'de' ? 'Team' : 'Team'}</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setAlertModal(p => ({ ...p, selectedTeam: p.home }))}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      alertModal.selectedTeam === alertModal.home ? 'bg-[#00BFA6]/20 border-[#00BFA6]/50 text-[#00BFA6]' : 'bg-white/5 border-white/10 text-gray-400'
                    }`}
                  >
                    {alertModal.home}
                  </button>
                  <button
                    onClick={() => setAlertModal(p => ({ ...p, selectedTeam: p.away }))}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      alertModal.selectedTeam === alertModal.away ? 'bg-[#00BFA6]/20 border-[#00BFA6]/50 text-[#00BFA6]' : 'bg-white/5 border-white/10 text-gray-400'
                    }`}
                  >
                    {alertModal.away}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-400 block mb-1">{lang === 'de' ? 'Quote-Schwelle' : 'Odds Threshold'}</label>
                <input
                  data-testid="alert-threshold"
                  type="number"
                  step="0.1"
                  min="1"
                  defaultValue="2.0"
                  onChange={(e) => setAlertModal(p => ({ ...p, threshold: parseFloat(e.target.value) || 2.0 }))}
                  className="w-full bg-[#121212] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00BFA6]/50"
                />
              </div>

              <div>
                <label className="text-xs text-gray-400 block mb-1">{lang === 'de' ? 'Benachrichtigen wenn' : 'Notify when'}</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setAlertModal(p => ({ ...p, direction: 'above' }))}
                    className={`flex-1 py-2 rounded-lg text-sm border transition-colors ${
                      (alertModal.direction || 'above') === 'above' ? 'bg-green-500/20 border-green-500/50 text-green-400' : 'bg-white/5 border-white/10 text-gray-400'
                    }`}
                  >
                    {lang === 'de' ? 'Quote steigt über' : 'Odds above'}
                  </button>
                  <button
                    onClick={() => setAlertModal(p => ({ ...p, direction: 'below' }))}
                    className={`flex-1 py-2 rounded-lg text-sm border transition-colors ${
                      alertModal.direction === 'below' ? 'bg-red-500/20 border-red-500/50 text-red-400' : 'bg-white/5 border-white/10 text-gray-400'
                    }`}
                  >
                    {lang === 'de' ? 'Quote fällt unter' : 'Odds below'}
                  </button>
                </div>
              </div>

              <Button
                data-testid="alert-create-btn"
                onClick={() => {
                  if (alertModal.selectedTeam) {
                    createAlert(
                      alertModal.match,
                      alertModal.sport,
                      alertModal.selectedTeam,
                      alertModal.threshold || 2.0,
                      alertModal.direction || 'above'
                    );
                  }
                }}
                disabled={!alertModal.selectedTeam}
                className="w-full bg-[#00BFA6] hover:bg-[#009E8A] text-black font-bold py-3 rounded-xl disabled:opacity-40"
              >
                <Bell className="w-4 h-4 mr-2" />
                {lang === 'de' ? 'Alert erstellen' : 'Create Alert'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <MobileBottomNav />
    </div>
  );
};

export default AnalyticsPage;
